
import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Button, Select, InputNumber, Typography, Alert } from 'antd';
import { CashFlow } from '../../types';
import axios, { AxiosError } from 'axios';

const { Title } = Typography;
const { Option } = Select;

interface AddEditCashFlowFormProps {
  visible: boolean;
  onClose: () => void;
  initialData?: CashFlow;
  onSubmit: (data: CashFlow) => Promise<void>; // Ensure onSubmit returns a Promise
}

interface ValidationError {
  property: string;
  constraints: { [key: string]: string };
}

interface ErrorResponse {
  statusCode: number;
  message: ValidationError[];
  error: string;
}

const AddEditCashFlowForm: React.FC<AddEditCashFlowFormProps> = ({
  visible,
  onClose,
  initialData,
  onSubmit,
}) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string>('');

  const isEditing = !!initialData;

  useEffect(() => {
    if (initialData) {
      form.setFieldsValue({
        name: initialData.name,
        description: initialData.description,
        type: initialData.type,
        amount: initialData.amount,
      });
    } else {
      form.resetFields();
    }
    setSubmitError(''); // Reset error state when form opens
  }, [initialData, form, visible]);

  const handleSubmit = async (values: any) => {
    setSubmitting(true);
    setSubmitError('');
    try {
      const data: CashFlow = {
        id: initialData?.id || '', // Backend should handle ID generation; adjust as needed
        estateId: initialData?.estateId || '', // Ensure estateId is set appropriately
        name: values.name,
        description: values.description,
        type: values.type,
        amount: values.amount,
      };
      await onSubmit(data); // Await the onSubmit Promise
      form.resetFields();
      onClose(); // Close the modal only on successful submission
    } catch (error: any) {
      console.error('Error submitting form:', error);

      // Check if the error is an AxiosError with a response
      if (axios.isAxiosError(error) && error.response) {
        const { status, data } = error.response;

        if (status === 400 && Array.isArray(data.message)) {
          const errorResponse = data as ErrorResponse;

          // Iterate through each validation error and set form field errors
          errorResponse.message.forEach((validationError) => {
            const { property, constraints } = validationError;
            const messages = Object.values(constraints).join(', '); // Combine multiple constraints
            form.setFields([
              {
                name: property,
                errors: [messages],
              },
            ]);
          });
        } else {
          // For other status codes or error formats, display a generic error message
          setSubmitError(data.message || 'An unexpected error occurred.');
        }
      } else {
        // For non-Axios errors, display a generic error message
        setSubmitError('Failed to submit form. Please try again.');
      }

      // The modal remains open, allowing the user to correct any issues
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      visible={visible}
      onCancel={onClose}
      footer={null}
      title={isEditing ? 'Edit Cash Flow' : 'Add Cash Flow'}
      destroyOnClose
      width={600}
      bodyStyle={{ padding: '24px' }}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={initialData || { type: 'Inbound', amount: 0 }}
      >
        {/* Display generic submission error if any */}
        {submitError && (
          <Alert
            message={submitError}
            type="error"
            showIcon
            style={{ marginBottom: '16px' }}
          />
        )}

        {/* CashFlow Identifiers Section */}
        <Title level={5}>CashFlow Identifiers</Title>
        <Form.Item
          label="Name"
          name="name"
          rules={[{ required: true, message: 'Please enter a name' }]}
        >
          <Input placeholder="Enter cash flow name" />
        </Form.Item>

        <Form.Item
          label="Description"
          name="description"
          rules={[{ required: true, message: 'Please enter a description' }]}
        >
          <Input.TextArea rows={3} placeholder="Enter cash flow description" />
        </Form.Item>

        {/* CashFlow Type and Payment Amount Section */}
        <Title level={5}>CashFlow Details</Title>
        <Form.Item
          label="CashFlow Type"
          name="type"
          rules={[{ required: true, message: 'Please select cash flow type' }]}
        >
          <Select placeholder="Select cash flow type">
            <Option value="Inbound">Inbound</Option>
            <Option value="Outbound">Outbound</Option>
          </Select>
        </Form.Item>

        <Form.Item
          label="Payment Amount"
          name="amount"
          rules={[
            { required: true, message: 'Please enter the payment amount' },
            { type: 'number', min: 0, message: 'Amount must be a positive number' },
          ]}
        >
          <InputNumber min={0} style={{ width: '100%' }} placeholder="Payment Amount" />
        </Form.Item>

        <Form.Item>
          <Button type="primary" htmlType="submit" loading={submitting} style={{ marginRight: '8px' }}>
            {isEditing ? 'Update Cash Flow' : 'Add Cash Flow'}
          </Button>
          <Button onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AddEditCashFlowForm;
